import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputBox from '../../atomic/inputbox/InputBox';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { updateTeamRoleAccessRegBranchAPI } from '../../context-api/api-services/UpdateTeamRoleAccessRegBranch.service';
import { sharedInputSx } from '../Products/kitchenProductsConstants';
import { ALL_ROUTES, BRAND, PALETTE, fieldSxMultiline } from './branchTeamsConfig';
import {
  buildBlankRouteAccess,
  mergeMemberRouteAccess,
  getTeamInfo,
  getTeamRoleAccess,
  parseCommaSeparatedList,
  formatTeamsErrors,
  formatApiMessage,
  avatarInitialSx,
  getInitials,
} from './branchTeamsUtils';
import { Transition, RouteAccessRow } from './BranchTeamsShared';

const kitchenField = { width: '100%', height: 'auto', ...sharedInputSx };
const kitchenFieldMultiline = { width: '100%', height: 'auto', ...fieldSxMultiline };
const kitchenReadOnlyRow = { ...sharedInputSx, flex: '1 1 220px' };

export function EditMemberModal({ open, onClose, onSuccess, member, branchName }) {
  const [docIsActive, setDocIsActive] = useState(true);
  const [manualNotes, setManualNotes] = useState('');
  const [routeAccess, setRouteAccess] = useState(buildBlankRouteAccess());
  const [initialSpecials, setInitialSpecials] = useState([]);
  const [specialKeptMap, setSpecialKeptMap] = useState({});
  const [specialPendingAdds, setSpecialPendingAdds] = useState([]);
  const [specialNewInput, setSpecialNewInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open || !member) return;
    setSaveError(null);
    const role = getTeamRoleAccess(member);
    const info = getTeamInfo(member);
    const dActive = role.is_active;
    setDocIsActive(typeof dActive === 'boolean' ? dActive : dActive !== false && dActive !== 'false' && dActive !== 0);
    const notes =
      role.manual_notes != null && String(role.manual_notes).trim() !== ''
        ? String(role.manual_notes)
        : info.manual_notes != null
          ? String(info.manual_notes)
          : '';
    setManualNotes(notes);
    setRouteAccess(mergeMemberRouteAccess(member));
    const raw = Array.isArray(role.special_condition_access) ? role.special_condition_access : [];
    const ordered = [];
    const seen = new Set();
    raw.forEach((x) => {
      const s = String(x ?? '').trim();
      if (!s) return;
      const k = s.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      ordered.push(s);
    });
    setInitialSpecials(ordered);
    const m = {};
    ordered.forEach((s) => {
      m[s.toLowerCase()] = true;
    });
    setSpecialKeptMap(m);
    setSpecialPendingAdds([]);
    setSpecialNewInput('');
  }, [open, member]);

  const infoForEdit = member ? getTeamInfo(member) : null;
  const fullName = infoForEdit
    ? [infoForEdit.firstName, infoForEdit.lastName].filter(Boolean).join(' ') || 'Member'
    : 'Member';

  const updateRoute = (idx, updated) => {
    setRouteAccess((prev) => prev.map((r, i) => (i === idx ? updated : r)));
  };

  const allReplaceActive = routeAccess.every((r) => r.is_active);
  const toggleAllReplaceRoutes = () => {
    setRouteAccess((prev) => prev.map((r) => ({ ...r, is_active: !allReplaceActive })));
  };

  const buildReplaceRoutePayload = () => {
    const rows = routeAccess.filter((r) => r.is_active);
    return rows.map((r) => ({
      route: String(r.route).trim().toLowerCase(),
      access: {
        read: !!r.access.read,
        write: !!r.access.write,
        edit: !!r.access.edit,
        delete: !!r.access.delete,
      },
    }));
  };

  const toggleSpecialKept = (keyLower) => {
    setSpecialKeptMap((prev) => {
      const on = prev[keyLower] !== false;
      return { ...prev, [keyLower]: !on };
    });
  };

  const addSpecialFromInput = () => {
    const parts = parseCommaSeparatedList(specialNewInput);
    if (!parts.length) return;
    setSpecialPendingAdds((prev) => {
      const merged = new Set(prev.map((p) => p.toLowerCase()));
      const initLower = new Set(initialSpecials.map((s) => s.toLowerCase()));
      parts.forEach((p) => {
        if (!initLower.has(p)) merged.add(p);
      });
      return [...merged];
    });
    setSpecialNewInput('');
  };

  const removePendingSpecial = (value) => {
    setSpecialPendingAdds((prev) => prev.filter((p) => p !== value));
  };

  const parseApiResult = (res) => {
    if (res.hasError) return { ok: false, message: res.error?.message || 'Request failed.' };
    const body = res.data;
    if (body?.success === false) {
      return {
        ok: false,
        message: formatTeamsErrors(body.errors) || formatApiMessage(body) || 'Update failed.',
      };
    }
    return { ok: true, body };
  };

  const handleSubmit = async () => {
    if (!member) return;
    const bn = String(branchName ?? '').trim().toLowerCase();
    const tid = String(getTeamInfo(member).team_user_id ?? '').trim().toLowerCase();
    if (!bn || !tid) {
      setSaveError('Branch name and team user ID are required.');
      return;
    }

    const ra = buildReplaceRoutePayload();
    if (ra.length === 0) {
      setSaveError('Turn on at least one page and set its permissions before saving.');
      return;
    }

    const notesTrim = manualNotes.trim();
    const removeSpecial = initialSpecials
      .filter((s) => specialKeptMap[String(s).trim().toLowerCase()] === false)
      .map((s) => String(s).trim().toLowerCase());

    const addSpecial = [...new Set(specialPendingAdds.map((a) => String(a).trim().toLowerCase()).filter(Boolean))];

    const basePayload = {
      branchName: bn,
      team_user_id: tid,
      is_active: !!docIsActive,
      routeAccess: ra,
    };
    if (notesTrim !== '') basePayload.manual_notes = notesTrim;

    setSaving(true);
    setSaveError(null);

    let lastMessage = null;

    const run = async (payload) => {
      const res = await updateTeamRoleAccessRegBranchAPI(payload);
      const out = parseApiResult(res);
      if (out.ok && out.body) lastMessage = formatApiMessage(out.body) || lastMessage;
      return out;
    };

    try {
      if (removeSpecial.length > 0 && addSpecial.length > 0) {
        const r1 = await run({ ...basePayload, remove_special_condition_access: removeSpecial });
        if (!r1.ok) {
          setSaveError(r1.message);
          setSaving(false);
          return;
        }
        const r2 = await run({
          branchName: bn,
          team_user_id: tid,
          is_active: !!docIsActive,
          special_condition_access: addSpecial,
        });
        if (!r2.ok) {
          setSaveError(r2.message);
          setSaving(false);
          return;
        }
      } else if (removeSpecial.length > 0) {
        const r1 = await run({ ...basePayload, remove_special_condition_access: removeSpecial });
        if (!r1.ok) {
          setSaveError(r1.message);
          setSaving(false);
          return;
        }
      } else if (addSpecial.length > 0) {
        const r1 = await run({ ...basePayload, special_condition_access: addSpecial });
        if (!r1.ok) {
          setSaveError(r1.message);
          setSaving(false);
          return;
        }
      } else {
        const r1 = await run(basePayload);
        if (!r1.ok) {
          setSaveError(r1.message);
          setSaving(false);
          return;
        }
      }

      setSaving(false);
      onSuccess?.(lastMessage);
      onClose();
    } catch (e) {
      setSaveError(e?.message || 'Something went wrong.');
      setSaving(false);
    }
  };

  if (!member) return null;

  const replaceActiveCount = routeAccess.filter((r) => r.is_active).length;
  const removalSpecialCount = initialSpecials.filter(
    (s) => specialKeptMap[String(s).trim().toLowerCase()] === false
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: '5px',
          boxShadow: '0 30px 80px rgba(44,26,14,0.20)',
          overflow: 'hidden',
          maxHeight: '94vh',
          border: `1px solid ${PALETTE.border}`,
        },
      }}
    >
      {/* ── clean cream dialog header — no dark background ── */}
      <DialogTitle sx={{ p: 0, bgcolor: PALETTE.tableHeadBg, borderBottom: `1px solid ${PALETTE.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '16px 20px' }}>
          <Avatar
            sx={avatarInitialSx({
              width: 44,
              height: 44,
              fontSize: '1rem',
              borderRadius: '10px',
              flexShrink: 0,
            })}
          >
            {getInitials(infoForEdit?.firstName, infoForEdit?.lastName)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '1rem', color: PALETTE.textPrimary, lineHeight: 1.3 }}>
              Edit Role Access
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: PALETTE.textMuted, mt: 0.2 }}>
              <Box component='span' sx={{ fontFamily: 'Inter Bold', color: PALETTE.textPrimary }}>{fullName}</Box>
              {' · '}Route & permissions update
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size='small'
            sx={{
              color: PALETTE.textMuted,
              bgcolor: '#fff',
              border: `1px solid ${PALETTE.border}`,
              borderRadius: '5px',
              '&:hover': { color: PALETTE.textPrimary, bgcolor: PALETTE.caramelLight, borderColor: PALETTE.caramelBorder },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: '20px 24px', overflowY: 'auto', bgcolor: '#fff' }}>
        {saveError && (
          <Alert severity='error' sx={{ mb: 2, borderRadius: '5px', fontSize: '0.82rem', bgcolor: '#FFF1F0', border: '1px solid #fca5a5' }}>
            {saveError}
          </Alert>
        )}

        <Alert
          icon={<InfoOutlinedIcon />}
          severity='info'
          sx={{ mb: 2.5, borderRadius: '5px', fontSize: '0.8rem', bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', color: '#0c4a6e' }}
        >
          Updates <strong>TeamRoleAccess</strong> record only (not profile email/phone).{' '}
          <code>dedicatedRole</code> is not editable on this route.
        </Alert>

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Required Identifiers
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
          <TextField label='Branch name' value={branchName || ''} fullWidth size='small' InputProps={{ readOnly: true }} sx={kitchenReadOnlyRow} />
          <TextField label='Team user ID' value={infoForEdit?.team_user_id || ''} fullWidth size='small' InputProps={{ readOnly: true }} sx={kitchenReadOnlyRow} />
        </Box>

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Profile (read-only)
        </Typography>
        <Paper
          variant='outlined'
          sx={{ p: 1.75, mb: 2.5, borderRadius: '5px', bgcolor: PALETTE.tableHeadBg, border: `1px solid ${PALETTE.border}` }}
        >
          <Typography sx={{ fontSize: '0.82rem', color: PALETTE.textSecondary }}>
            <Box component='span' sx={{ fontFamily: 'Inter Bold', color: PALETTE.textPrimary }}>{fullName}</Box>
            {infoForEdit?.email ? <> · {infoForEdit.email}</> : null}
            {infoForEdit?.phone_no != null && infoForEdit.phone_no !== '' ? <> · {infoForEdit.phone_no}</> : null}
          </Typography>
          {infoForEdit?.user_type != null && infoForEdit.user_type !== '' && (
            <Typography sx={{ fontSize: '0.75rem', color: PALETTE.textMuted, mt: 0.5 }}>
              User type: {String(infoForEdit.user_type)}
              {(() => {
                const dr = getTeamRoleAccess(member).dedicatedRole ?? infoForEdit?.dedicatedRole;
                return dr != null && dr !== '' ? ` · Dedicated role (read-only): ${dr}` : '';
              })()}
            </Typography>
          )}
        </Paper>

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Record Status
        </Typography>
        <Paper
          variant='outlined'
          sx={{ p: 1.75, mb: 2.5, borderRadius: '5px', border: `1px solid ${PALETTE.border}`, bgcolor: docIsActive ? '#F0FDF4' : PALETTE.tableHeadBg }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={docIsActive}
                onChange={(e) => setDocIsActive(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16a34a' },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.85rem', color: PALETTE.textPrimary }}>
                  Team role access record is active
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: PALETTE.textMuted }}>
                  Sends boolean <code>is_active</code> on the TeamRoleAccess document.
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Manual Notes
        </Typography>
        <InputBox
          label='Manual notes'
          name='manual_notes'
          value={manualNotes}
          onChange={(e) => setManualNotes(e.target.value)}
          width='100%'
          height='auto'
          sx={{ ...kitchenFieldMultiline, mb: 2 }}
          multiline
          minRows={2}
          inputErrorMsg=''
          helperText='Server stores trimmed / lowercased text.'
        />

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Page Access
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: PALETTE.textMuted, mb: 1.5 }}>
          Toggle pages on to grant access, then set read / write / edit / delete permissions per page.
        </Typography>
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', color: PALETTE.textMuted }}>
              {replaceActiveCount} of {ALL_ROUTES.length} pages enabled
            </Typography>
            <Button
              size='small'
              onClick={toggleAllReplaceRoutes}
              sx={{
                fontFamily: 'Inter Bold',
                fontSize: '0.72rem',
                textTransform: 'none',
                color: allReplaceActive ? '#ef4444' : BRAND.orange,
                '&:hover': { bgcolor: allReplaceActive ? '#FFF1F0' : PALETTE.caramelLight },
                borderRadius: '5px',
              }}
            >
              {allReplaceActive ? 'Turn off all pages' : 'Turn on all pages'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ALL_ROUTES.map((r, idx) => (
              <RouteAccessRow key={r.route} entry={routeAccess[idx]} label={r.label} onChange={(u) => updateRoute(idx, u)} />
            ))}
          </Box>
        </Box>

        <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.7rem', color: PALETTE.textMuted, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>
          Special Conditions
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: PALETTE.textMuted, mb: 1.5 }}>
          Existing conditions stay on unless you toggle them off. Add new ones below — applied when you save.
        </Typography>
        <Paper variant='outlined' sx={{ p: 2, mb: 2, borderRadius: '5px', bgcolor: '#fff', border: `1px solid ${PALETTE.border}` }}>
          {initialSpecials.length === 0 ? (
            <Typography sx={{ fontSize: '0.82rem', color: PALETTE.textMuted }}>No special conditions on this member yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {initialSpecials.map((s) => {
                const k = s.toLowerCase();
                const on = specialKeptMap[k] !== false;
                return (
                  <Box
                    key={k}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 0.75,
                      px: 1,
                      borderRadius: '5px',
                      bgcolor: on ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${on ? '#bbf7d0' : '#fecaca'}`,
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.85rem', color: PALETTE.textPrimary }}>{s}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={on}
                          onChange={() => toggleSpecialKept(k)}
                          size='small'
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16a34a' },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.78rem', color: PALETTE.textMuted }}>{on ? 'On' : 'Remove on save'}</Typography>}
                      sx={{ mr: 0 }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          {removalSpecialCount > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: '#b45309', mt: 1.5, fontFamily: 'Inter Regular' }}>
              {removalSpecialCount} condition{removalSpecialCount !== 1 ? 's' : ''} will be removed when you save.
            </Typography>
          )}
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'flex-start' }, mb: 2 }}>
          <InputBox
            label='Add new conditions'
            name='specialNewInput'
            placeholder='Comma-separated, e.g. 60088, can_export'
            value={specialNewInput}
            onChange={(e) => setSpecialNewInput(e.target.value)}
            width='100%'
            height='auto'
            sx={{ ...kitchenField, flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSpecialFromInput();
              }
            }}
          />
          <Button
            variant='outlined'
            onClick={addSpecialFromInput}
            sx={{ fontFamily: 'Inter Bold', textTransform: 'none', borderRadius: '5px', flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}
          >
            Add
          </Button>
        </Box>
        {specialPendingAdds.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.7rem', color: PALETTE.textMuted, fontFamily: 'Inter Bold', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
              New — will be added on save
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {specialPendingAdds.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  onDelete={() => removePendingSpecial(p)}
                  size='small'
                  sx={{
                    fontFamily: 'Inter Regular',
                    fontSize: '0.78rem',
                    bgcolor: PALETTE.caramelLight,
                    color: PALETTE.textPrimary,
                    border: `1px solid ${PALETTE.caramelBorder}`,
                    borderRadius: '5px',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap', bgcolor: PALETTE.tableHeadBg, borderTop: `1px solid ${PALETTE.border}` }}>
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{
            borderColor: PALETTE.border,
            color: PALETTE.textSecondary,
            fontFamily: 'Inter Bold',
            borderRadius: '5px',
            textTransform: 'none',
            px: 2.5,
            '&:hover': { borderColor: PALETTE.caramelBorder, bgcolor: PALETTE.caramelLight },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CheckCircleRoundedIcon />}
          sx={{
            bgcolor: BRAND.orange,
            color: '#fff',
            fontFamily: 'Inter Bold',
            borderRadius: '5px',
            textTransform: 'none',
            px: 3,
            boxShadow: '0 4px 14px rgba(252,93,27,0.35)',
            '&:hover': { bgcolor: '#e84e10', boxShadow: '0 6px 20px rgba(252,93,27,0.45)' },
            '&.Mui-disabled': { opacity: 0.65, color: '#fff' },
          }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
